import React, { useState, useCallback } from 'react';
import {
  Title,
  Group,
  Button,
  Text,
  Badge,
  Stack,
  Grid,
  Card,
  Divider,
  ActionIcon,
  LoadingOverlay,
  Alert,
  SimpleGrid,
  Progress,
  Tabs,
  Timeline,
  Switch,
  Slider,
  Indicator,
} from '@mantine/core';
import {
  IconEdit,
  IconArrowLeft,
  IconCamera,
  IconMapPin,
  IconWifi,
  IconWifiOff,
  IconActivity,
  IconAlertTriangle,
  IconSettings,
  IconVideo,
  IconTestPipe,
  IconPlayerPlay,
  IconPlayerPause,
  IconEye,
  IconClock,
  IconShield,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useCamera, useToggleCameraStatus, useTestCameraConnection, useUpdateCamera } from '../../hooks/useCameras';
import RTSPVideoPlayer from '../../components/video/RTSPVideoPlayer';

const CameraDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [isStreaming, setIsStreaming] = useState(false);

  const {
    data: camera,
    isLoading: cameraLoading,
    error: cameraError,
  } = useCamera(id || '');

  // Helper functions to safely access nested data
  const getCameraStatus = () => camera?.status || 'unknown';
  const getCameraHealthStatus = () => camera?.health_status || 'unknown';
  const getCameraActive = () => camera?.active || camera?.status_details?.active || false;
  const getCameraLocation = () => camera?.location?.area || {};
  const getCameraRtspUrl = () => camera?.rtsp_url || '';
  const getCameraResolution = () => camera?.resolution || camera?.technical_specs?.resolution || 'Unknown';
  const getCameraFps = () => camera?.fps || camera?.technical_specs?.fps || 30;

  const toggleStatusMutation = useToggleCameraStatus();
  const testConnectionMutation = useTestCameraConnection();
  const updateCameraMutation = useUpdateCamera();

  const handleEdit = () => {
    navigate(`/admin/cameras/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/admin/cameras');
  };

  const handleToggleStatus = async () => {
    if (!camera) return;
    try {
      await toggleStatusMutation.mutateAsync(camera.id);
      notifications.show({
        title: 'Camera Status Updated',
        message: `Camera ${camera.is_active ? 'disabled' : 'enabled'} successfully`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to toggle camera status. Please try again.',
        color: 'red',
      });
    }
  };

  const handleTestConnection = async () => {
    if (!camera) return;
    try {
      await testConnectionMutation.mutateAsync(camera.id);
      notifications.show({
        title: 'Connection Test',
        message: 'Camera connection test completed successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Connection Test Failed',
        message: 'Unable to connect to camera. Please check the RTSP URL and network settings.',
        color: 'red',
      });
    }
  };

  // Enhanced stream error handling
  const handleStreamError = useCallback((error: string) => {
    console.error('RTSP Stream Error:', error);
    notifications.show({
      title: 'Stream Error',
      message: 'Unable to connect to camera stream. Please check the camera status and try again.',
      color: 'red',
    });
    setIsStreaming(false);
  }, []);

  // Enhanced stream status handling
  const handleStreamStatusChange = useCallback((status: 'loading' | 'playing' | 'paused' | 'error' | 'stopped') => {
    console.log('Stream Status:', status);
    setIsStreaming(status === 'playing');

    if (status === 'playing') {
      notifications.show({
        title: 'Stream Connected',
        message: 'Camera stream is now playing',
        color: 'green',
      });
    } else if (status === 'error') {
      notifications.show({
        title: 'Stream Error',
        message: 'Failed to load camera stream',
        color: 'red',
      });
    }
  }, []);

  const handleSettingChange = async (setting: string, value: any) => {
    if (!camera) return;

    try {
      await updateCameraMutation.mutateAsync({
        id: camera.id,
        data: {
          [setting]: value,
        },
      });
    } catch (error) {
      // Error handling: Failed to update camera settings
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'green',
      inactive: 'red',
      error: 'orange',
      maintenance: 'blue',
    };
    return colors[status] || 'gray';
  };

  const getHealthStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      healthy: 'green',
      warning: 'yellow',
      error: 'red',
      offline: 'red',
      unknown: 'gray',
    };
    return colors[status] || 'gray';
  };

  const formatLastActivity = (lastActivity: string | undefined) => {
    if (!lastActivity) return 'Never';
    const date = new Date(lastActivity);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (cameraError) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error loading camera"
        color="red"
      >
        Failed to load camera details. Please try again.
      </Alert>
    );
  }

  if (cameraLoading || !camera) {
    return <LoadingOverlay visible />;
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Group>
          <ActionIcon variant="subtle" onClick={handleBack}>
            <IconArrowLeft size={16} />
          </ActionIcon>
          <div>
            <Title order={2}>{camera.name}</Title>
            <Text size="sm" c="dimmed">
              Camera Details & Configuration
            </Text>
          </div>
        </Group>
        <Group>
          <Button
            variant="outline"
            leftSection={<IconTestPipe size={16} />}
            onClick={handleTestConnection}
            loading={testConnectionMutation.isPending}
          >
            Test Connection
          </Button>
          <Button
            leftSection={<IconEdit size={16} />}
            onClick={handleEdit}
          >
            Edit Camera
          </Button>
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconCamera size={14} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="stream" leftSection={<IconVideo size={14} />}>
            Live Stream
          </Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={14} />}>
            Settings
          </Tabs.Tab>
          <Tabs.Tab value="activity" leftSection={<IconActivity size={14} />}>
            Activity Log
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <Grid>
            {/* Main Camera Information */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="md">
                {/* Basic Info Card */}
                <Card>
                  <Group mb="md">
                    <Indicator
                      color={getStatusColor(getCameraStatus())}
                      position="bottom-end"
                      size={16}
                      processing={getCameraStatus() === 'active'}
                    >
                      <IconCamera size={40} />
                    </Indicator>
                    <div style={{ flex: 1 }}>
                      <Group justify="space-between" align="flex-start">
                        <div>
                          <Title order={3}>{camera?.name || 'Unknown Camera'}</Title>
                          {camera?.description && (
                            <Text c="dimmed" size="sm">
                              {camera.description}
                            </Text>
                          )}
                          <Text c="dimmed" size="sm">
                            Camera ID: {camera?.id || 'Unknown'}
                          </Text>
                        </div>
                        <Group gap="xs">
                          <Badge
                            color={getStatusColor(getCameraStatus())}
                            variant="light"
                            leftSection={
                              getCameraStatus() === 'active' ?
                                <IconWifi size={12} /> :
                                <IconWifiOff size={12} />
                            }
                          >
                            {getCameraStatus()}
                          </Badge>
                          <Badge
                            color={getHealthStatusColor(getCameraHealthStatus())}
                            variant="light"
                          >
                            Health: {getCameraHealthStatus()}
                          </Badge>
                        </Group>
                      </Group>
                    </div>
                  </Group>

                  <Divider mb="md" />

                  <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                    <Group>
                      <IconMapPin size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Area</Text>
                        <Text size="sm">{getCameraLocation().name || 'Not assigned'}</Text>
                        {getCameraLocation().area_type && (
                          <Text size="xs" c="dimmed" tt="capitalize">
                            {getCameraLocation().area_type}
                          </Text>
                        )}
                      </div>
                    </Group>

                    <Group>
                      <IconVideo size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Resolution</Text>
                        <Text size="sm">{getCameraResolution()}</Text>
                        <Text size="xs" c="dimmed">{getCameraFps()} FPS</Text>
                      </div>
                    </Group>

                    <Group>
                      <IconActivity size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Detections (24h)</Text>
                        <Text size="sm">{camera?.activity_stats?.activities_24h || 0} total</Text>
                      </div>
                    </Group>

                    <Group>
                      <IconClock size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Last Activity</Text>
                        <Text size="sm">
                          {formatLastActivity(camera?.activity_stats?.last_activity)}
                        </Text>
                      </div>
                    </Group>

                    <Group>
                      <IconShield size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Area Type</Text>
                        <Text size="sm" tt="capitalize">
                          {getCameraLocation().area_type || 'Unknown'}
                        </Text>
                      </div>
                    </Group>

                    <Group>
                      <IconEye size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">AI Detection</Text>
                        <Text size="sm">
                          {camera?.ai_detection_enabled ? 'Enabled' : 'Disabled'}
                        </Text>
                      </div>
                    </Group>
                  </SimpleGrid>
                </Card>

                {/* Stream Information */}
                <Card>
                  <Title order={4} mb="md">Stream Information</Title>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <div>
                      <Text size="xs" c="dimmed">RTSP URL</Text>
                      <Text size="sm" style={{ wordBreak: 'break-all' }}>
                        {getCameraRtspUrl() || 'Not configured'}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Stream Quality</Text>
                      <Text size="sm">
                        {camera?.stream_quality || camera?.technical_specs?.stream_quality?.label || 'Standard'}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Recording</Text>
                      <Text size="sm">
                        {camera?.recording_enabled ? 'Enabled' : 'Disabled'}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Codec</Text>
                      <Text size="sm">
                        {camera?.codec || camera?.technical_specs?.codec || 'H.264'}
                      </Text>
                    </div>
                  </SimpleGrid>
                </Card>

                {/* Live Stream Preview */}
                <Card>
                  <Group justify="space-between" mb="md">
                    <Title order={4}>Live Stream Preview</Title>
                    <Badge
                      color={getCameraRtspUrl() ? (getCameraActive() ? 'green' : 'yellow') : 'red'}
                      variant="light"
                    >
                      {getCameraRtspUrl() ? (getCameraActive() ? 'Available' : 'Camera Offline') : 'Not Configured'}
                    </Badge>
                  </Group>

                  {getCameraRtspUrl() ? (
                    <Stack gap="md">
                      <div
                        style={{
                          width: '100%',
                          height: '200px',
                          backgroundColor: '#000',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        <Stack align="center" gap="sm">
                          <IconVideo size={48} color="white" />
                          <Text c="white" size="sm" ta="center">
                            Click "View Live Stream" to start streaming
                          </Text>
                        </Stack>
                      </div>

                      <Button
                        variant="filled"
                        leftSection={<IconVideo size={16} />}
                        onClick={() => {
                          setActiveTab('stream');
                          notifications.show({
                            title: 'Loading Stream',
                            message: 'Connecting to camera stream...',
                            color: 'blue',
                          });
                        }}
                        fullWidth
                        size="md"
                      >
                        View Live Stream
                      </Button>
                    </Stack>
                  ) : (
                    <Stack align="center" gap="md" py="xl">
                      <IconCamera size={48} color="gray" />
                      <Text c="dimmed" ta="center">
                        No RTSP URL configured for this camera
                      </Text>
                      <Button
                        variant="outline"
                        leftSection={<IconSettings size={16} />}
                        onClick={() => setActiveTab('settings')}
                      >
                        Configure Stream URL
                      </Button>
                    </Stack>
                  )}
                </Card>

                {/* Position Information */}
                {camera.position && (
                  <Card>
                    <Title order={4} mb="md">Position & Orientation</Title>
                    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                      <div>
                        <Text size="xs" c="dimmed">X Position</Text>
                        <Text size="sm">{camera.position.x || 'N/A'}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Y Position</Text>
                        <Text size="sm">{camera.position.y || 'N/A'}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Z Position</Text>
                        <Text size="sm">{camera.position.z || 'N/A'}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Rotation</Text>
                        <Text size="sm">{camera.position.rotation || 0}°</Text>
                      </div>
                    </SimpleGrid>
                  </Card>
                )}
              </Stack>
            </Grid.Col>

            {/* Sidebar */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                {/* Quick Actions */}
                <Card>
                  <Title order={4} mb="md">Quick Actions</Title>
                  <Stack gap="md">
                    <Button
                      variant={getCameraActive() ? 'filled' : 'outline'}
                      color={getCameraActive() ? 'red' : 'green'}
                      leftSection={
                        getCameraActive() ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />
                      }
                      onClick={handleToggleStatus}
                      loading={toggleStatusMutation.isPending}
                      fullWidth
                    >
                      {getCameraActive() ? 'Disable Camera' : 'Enable Camera'}
                    </Button>

                    <Button
                      variant="outline"
                      leftSection={<IconVideo size={16} />}
                      onClick={() => {
                        setActiveTab('stream');
                        notifications.show({
                          title: 'Loading Stream',
                          message: 'Connecting to camera stream...',
                          color: 'blue',
                        });
                      }}
                      fullWidth
                    >
                      View Live Stream
                    </Button>

                    <Button
                      variant="outline"
                      leftSection={<IconSettings size={16} />}
                      onClick={() => setActiveTab('settings')}
                      fullWidth
                    >
                      Camera Settings
                    </Button>
                  </Stack>
                </Card>

                {/* Health Status */}
                <Card>
                  <Title order={4} mb="md">Health Status</Title>
                  <Stack gap="md">
                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm">Overall Health</Text>
                        <Badge
                          color={getHealthStatusColor(getCameraHealthStatus())}
                          variant="light"
                          size="sm"
                        >
                          {getCameraHealthStatus()}
                        </Badge>
                      </Group>
                      <Progress
                        value={
                          getCameraHealthStatus() === 'healthy' ? 100 :
                          getCameraHealthStatus() === 'warning' ? 70 : 30
                        }
                        color={getHealthStatusColor(getCameraHealthStatus())}
                        size="sm"
                      />
                    </div>

                    <Group justify="space-between">
                      <Text size="sm">Last Health Check</Text>
                      <Text size="sm" c="dimmed">
                        {camera?.health_metrics?.last_health_check
                          ? formatLastActivity(camera.health_metrics.last_health_check)
                          : 'Never'
                        }
                      </Text>
                    </Group>

                    {getCameraHealthStatus() !== 'healthy' && (
                      <Alert
                        icon={<IconAlertTriangle size={16} />}
                        color="orange"
                      >
                        Camera requires attention
                      </Alert>
                    )}
                  </Stack>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <Title order={4} mb="md">Recent Activity (24h)</Title>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text size="sm">Total Activities</Text>
                      <Badge variant="light" size="lg">
                        {camera?.activity_stats?.activities_24h || 0}
                      </Badge>
                    </Group>

                    {camera?.activity_stats?.activity_types && Object.keys(camera.activity_stats.activity_types).length > 0 ? (
                      <Stack gap="xs">
                        <Text size="sm" fw={500}>Activity Types</Text>
                        {Object.entries(camera.activity_stats.activity_types).map(([type, count]) => (
                          <Group key={type} justify="space-between">
                            <Group gap="xs">
                              <IconActivity size={14} color="blue" />
                              <Text size="sm" tt="capitalize">{type}</Text>
                            </Group>
                            <Badge variant="outline" size="sm">
                              {String(count)}
                            </Badge>
                          </Group>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="xs" c="dimmed" ta="center" py="md">
                        No activities detected in the last 24 hours
                      </Text>
                    )}

                    {camera?.activity_stats?.verification_stats && (
                      <Stack gap="xs">
                        <Text size="sm" fw={500}>Verification Status</Text>
                        {Object.entries(camera.activity_stats.verification_stats).map(([status, count]) => (
                          <Group key={status} justify="space-between">
                            <Text size="sm" tt="capitalize">{status}</Text>
                            <Badge
                              variant="light"
                              color={status === 'verified' ? 'green' : 'orange'}
                              size="sm"
                            >
                              {String(count)}
                            </Badge>
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="stream" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, lg: 8 }}>
              {getCameraRtspUrl() ? (
                <RTSPVideoPlayer
                  rtspUrl={getCameraRtspUrl()}
                  cameraName={camera?.name || 'Unknown Camera'}
                  cameraId={camera?.id || ''}
                  autoPlay={true}
                  showControls={true}
                  showStatus={true}
                  height={500}
                  onError={handleStreamError}
                  onStatusChange={handleStreamStatusChange}
                />
              ) : (
                <Card withBorder style={{ height: '500px' }}>
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                    }}
                  >
                    <Stack align="center" gap="md">
                      <IconCamera size={64} color="gray" />
                      <Text c="white" ta="center">
                        No RTSP URL configured for this camera
                      </Text>
                      <Text c="gray" size="sm" ta="center">
                        Please configure the RTSP URL in camera settings to view the live stream
                      </Text>
                      <Button
                        variant="outline"
                        color="blue"
                        leftSection={<IconSettings size={16} />}
                        onClick={() => setActiveTab('settings')}
                      >
                        Configure Camera
                      </Button>
                    </Stack>
                  </div>
                </Card>
              )}
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 4 }}>
              <Stack gap="md">
                <Card>
                  <Title order={5} mb="md">Stream Information</Title>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">RTSP URL:</Text>
                      <Text size="sm" style={{ wordBreak: 'break-all' }}>
                        {getCameraRtspUrl() || 'Not configured'}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Resolution:</Text>
                      <Text size="sm">{getCameraResolution()}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">FPS:</Text>
                      <Text size="sm">{getCameraFps()}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Codec:</Text>
                      <Text size="sm">{camera?.codec || camera?.technical_specs?.codec || 'H.264'}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Stream Status:</Text>
                      <Badge
                        color={isStreaming ? 'green' : getCameraActive() ? 'yellow' : 'red'}
                        variant="light"
                        size="sm"
                      >
                        {isStreaming ? 'Streaming' : getCameraActive() ? 'Ready' : 'Offline'}
                      </Badge>
                    </Group>
                  </Stack>
                </Card>

                <Card>
                  <Title order={5} mb="md">Stream Controls</Title>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text size="sm">Recording</Text>
                      <Switch
                        checked={camera?.recording_enabled || false}
                        onChange={(event) => {
                          // Handle recording toggle
                          console.log('Recording toggle:', event.currentTarget.checked);
                        }}
                      />
                    </Group>

                    <Group justify="space-between">
                      <Text size="sm">AI Detection</Text>
                      <Switch
                        checked={camera?.ai_detection_enabled || false}
                        onChange={(event) => {
                          // Handle AI detection toggle
                          console.log('AI Detection toggle:', event.currentTarget.checked);
                        }}
                      />
                    </Group>

                    <Divider />

                    <Button
                      variant="outline"
                      leftSection={<IconTestPipe size={16} />}
                      onClick={handleTestConnection}
                      loading={testConnectionMutation.isPending}
                      disabled={!getCameraRtspUrl()}
                      fullWidth
                    >
                      Test Stream Connection
                    </Button>

                    {!getCameraRtspUrl() && (
                      <Alert color="orange">
                        <Text size="xs">
                          Configure RTSP URL in settings to test connection
                        </Text>
                      </Alert>
                    )}
                  </Stack>
                </Card>

                <Card>
                  <Title order={5} mb="md">Stream Quality</Title>
                  <Stack gap="md">
                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm">Bitrate</Text>
                        <Text size="xs" c="dimmed">
                          {isStreaming ? '3.2 Mbps' : 'Not streaming'}
                        </Text>
                      </Group>
                      <Progress
                        value={isStreaming ? 75 : 0}
                        color={isStreaming ? "blue" : "gray"}
                      />
                    </div>

                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm">Signal Strength</Text>
                        <Text size="xs" c="dimmed">
                          {isStreaming ? 'Excellent' : getCameraActive() ? 'Ready' : 'Offline'}
                        </Text>
                      </Group>
                      <Progress
                        value={isStreaming ? 85 : getCameraActive() ? 50 : 0}
                        color={isStreaming ? "green" : getCameraActive() ? "yellow" : "red"}
                      />
                    </div>

                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm">Stream Latency</Text>
                        <Text size="xs" c="dimmed">
                          {isStreaming ? '~2s' : 'N/A'}
                        </Text>
                      </Group>
                      <Progress
                        value={isStreaming ? 60 : 0}
                        color={isStreaming ? "orange" : "gray"}
                      />
                    </div>
                  </Stack>
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="settings" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="md">
                <Card>
                  <Title order={4} mb="md">Detection Settings</Title>
                  <Stack gap="md">
                    <Switch
                      label="Motion Detection"
                      description="Enable automatic motion detection"
                      checked={camera.settings?.motion_detection_enabled || false}
                      onChange={(event) =>
                        handleSettingChange('motion_detection_enabled', event.currentTarget.checked)
                      }
                    />

                    <div>
                      <Text size="sm" mb="xs">Detection Sensitivity</Text>
                      <Slider
                        value={camera.settings?.detection_sensitivity || 50}
                        onChange={(value) => handleSettingChange('detection_sensitivity', value)}
                        min={0}
                        max={100}
                        step={10}
                        marks={[
                          { value: 0, label: 'Low' },
                          { value: 50, label: 'Medium' },
                          { value: 100, label: 'High' }
                        ]}
                      />
                    </div>
                  </Stack>
                </Card>

                <Card>
                  <Title order={4} mb="md">Recording Settings</Title>
                  <Stack gap="md">
                    <Switch
                      label="Audio Recording"
                      description="Record audio along with video"
                      checked={camera.settings?.audio_recording_enabled || false}
                      onChange={(event) =>
                        handleSettingChange('audio_recording_enabled', event.currentTarget.checked)
                      }
                    />

                    <Switch
                      label="Night Vision"
                      description="Enable infrared night vision mode"
                      checked={camera.settings?.night_vision_enabled || false}
                      onChange={(event) =>
                        handleSettingChange('night_vision_enabled', event.currentTarget.checked)
                      }
                    />
                  </Stack>
                </Card>
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card>
                <Title order={4} mb="md">Settings Help</Title>
                <Stack gap="xs">
                  <Text size="sm">• Motion detection helps reduce false alerts</Text>
                  <Text size="sm">• Higher sensitivity may increase false positives</Text>
                  <Text size="sm">• Night vision works best in low light</Text>
                  <Text size="sm">• Audio recording requires additional storage</Text>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="activity" pt="md">
          <Card>
            <Group justify="space-between" mb="md">
              <Title order={4}>Activity Timeline</Title>
              <Button variant="subtle" size="xs">
                View All
              </Button>
            </Group>

            <Timeline>
              <Timeline.Item title="Camera Online">
                <Text c="dimmed" size="sm">
                  Camera came online and started streaming
                </Text>
                <Text size="xs" c="dimmed">
                  2 hours ago
                </Text>
              </Timeline.Item>

              <Timeline.Item title="Health Check Completed">
                <Text c="dimmed" size="sm">
                  Automated health check passed successfully
                </Text>
                <Text size="xs" c="dimmed">
                  6 hours ago
                </Text>
              </Timeline.Item>

              <Timeline.Item title="Settings Updated">
                <Text c="dimmed" size="sm">
                  Motion detection sensitivity adjusted
                </Text>
                <Text size="xs" c="dimmed">
                  1 day ago
                </Text>
              </Timeline.Item>
            </Timeline>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

export default CameraDetail;
